import { createContext, useContext } from 'react'
import type { Event } from '@/types'

// #294 redesign — what the one critical alerts controller (CriticalAlertsProvider, mounted once in
// MainLayout) gives the alert bar, the popup stack and the fleet map. They only read it: the controller
// alone owns the open-critical query, the sound (and with it the tab's sound leadership), the tab title
// and the tab icon badge. Outside the provider (tests of other components, a page without the layout)
// it reads as "nothing open".

export interface CriticalAlertsValue {
  /** The open critical alerts loaded (up to 50), newest first. */
  open: Event[]
  /** How many are open in all (the API's count). */
  total: number
  /** When the open list was last fetched (the query's dataUpdatedAt). */
  fetchedAt: number
  /** Whether this alert is still inside its sound window (decides whether its popup appears). */
  insideWindow: (e: Event) => boolean
  /** "Quiet 2 min" for every tab of this browser. (The seconds left are in CriticalQuietLeftContext.) */
  quiet: () => void
  /** An alert is open and the browser still blocks sound ("Click to enable sound"). */
  soundBlocked: boolean
  /** Acknowledge one alert (optimistic). */
  acknowledge: (id: number) => void
  /** The alert being acknowledged now, if any. */
  acknowledgingId: number | null
  /** Acknowledge every open alert up to the newest one loaded. */
  acknowledgeAll: () => void
  acknowledgingAll: boolean
  /** The popups on screen now (at most 3, newest first). */
  popups: Event[]
  /** Open alerts beyond the popups shown ("+ N more critical alerts": all open − popups shown, while at least
   *  one popup shows); 0 = no such line. */
  morePopups: number
  /** ✕ on a popup: hide it in this tab (the alert stays open). */
  hidePopup: (id: number) => void
}

const noop = () => {}

export const NO_CRITICAL_ALERTS: CriticalAlertsValue = {
  open: [],
  total: 0,
  fetchedAt: 0,
  insideWindow: () => false,
  quiet: noop,
  soundBlocked: false,
  acknowledge: noop,
  acknowledgingId: null,
  acknowledgeAll: noop,
  acknowledgingAll: false,
  popups: [],
  morePopups: 0,
  hidePopup: noop,
}

export const CriticalAlertsContext = createContext<CriticalAlertsValue>(NO_CRITICAL_ALERTS)

export function useCriticalAlertsContext(): CriticalAlertsValue {
  return useContext(CriticalAlertsContext)
}

/** Whole seconds of "Quiet 2 min" left (0 = not quiet). Its own context (review 2026-10-12): it changes every
 *  second during a quiet time and only the alert bar shows it, so the fleet map and the popups, which read
 *  CriticalAlertsContext, do not re-render on each tick. */
export const CriticalQuietLeftContext = createContext<number>(0)

export function useCriticalQuietLeft(): number {
  return useContext(CriticalQuietLeftContext)
}
